import { Outlet, useParams } from 'react-router-dom';
import styles from './SingleAsset.module.scss';
import { useAppSelector } from '@customer/store/hooks';
import { SystemTypePpu, useGetSingleAssetsQuery } from '@customer/store/apis/assetsApi';
import { skipToken, TagDescription } from '@reduxjs/toolkit/query';
import { ConnectivitySnackbar } from '@dmc/ui';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Permission, userHasPermission } from '@customer/permissions';
import NavTabs from '@cas/components/tabs/nav-tabs/NavTabs';
import Page from '@customer/templates/page/Page';
import { useAssetGroupChips } from '@customer/hooks/assets/useAssetGroupChips';
import { useAssetOverviewErrors } from '@customer/hooks/assets/useAssetOverviewErrors';
import AssetIdentifier from '@customer/components/asset-data/asset-identifier/AssetIdentifier';
import RefreshButton from '@customer/components/inputs/refresh-button/RefreshButton';
import { QueryTag } from '@customer/store/api';
import { NavTabModel } from '@cas/components/tabs/nav-tab/NavTab';
import { getHeatmapTag } from '@customer/store/apis/heatmapsApi';
import { useSiteSwitchWatcher } from '@customer/hooks/useSiteSwitchWatcher';

export function SingleAsset() {
  const { assetId } = useParams();
  const { t } = useTranslation();

  useSiteSwitchWatcher('/assets');

  const userData = useAppSelector(state => state.auth);

  //const { currentData: asset, error: fetchError } = useGetSingleAssetsQuery(assetId ?? skipToken, { refetchOnMountOrArgChange: 10 });
  const { currentData: asset, error: fetchError } = useGetSingleAssetsQuery(assetId ?? skipToken);

  const vehicleGroupChips = useAssetGroupChips({ assetGroups: asset?.ags });
  const assetOverviewErrors = useAssetOverviewErrors({ asset });

  const tabs = useMemo<NavTabModel[]>(() => {
    const items: NavTabModel[] = [];

    if (asset?.hw !== SystemTypePpu) {
      items.push({
        title: t('SelfTest'),
        to: `/asset/${assetId}/self-test`,
        isDotted: assetOverviewErrors.levelOneFaultsActive,
      });
      items.push({
        title: t('Configuration'),
        to: `/asset/${assetId}/configuration`,
        isDotted: assetOverviewErrors.config,
      });
    }

    items.push({
      title: t('AssetDetails'),
      to: `/asset/${assetId}/details`,
      isDotted: assetOverviewErrors.reporting,
    });

    if (userHasPermission(userData, Permission.PerformanceRead)) {
      items.push({
        title: t('Interactions'),
        to: `/asset/${assetId}/interactions`,
        isDotted: assetOverviewErrors.interactions,
      });
    }

    items.push({ title: t('Activity'), to: `/asset/${assetId}/activity` });

    if (userHasPermission(userData, Permission.FlagPreview)) {
      items.push({ title: t('Terminal'), to: `/asset/${assetId}/terminal` });

      if (asset?.inst) {
        items.push({ title: t('Installer Config'), to: `/asset/${assetId}/installer-config` });
      }
    }

    return items;
  }, [asset?.hw, asset?.inst, assetId, assetOverviewErrors, t, userData]);

  const tagsToClearOnRefresh = useMemo<TagDescription<QueryTag>[]>(
    () => [
      { type: QueryTag.Asset, id: assetId },
      { type: QueryTag.AssetFaults, id: assetId },
      { type: QueryTag.AssetSelfTestSummary, id: assetId },
      { type: QueryTag.AssetInteractions, id: assetId },
      { type: QueryTag.AssetVehicleInteractions, id: assetId },
      { type: QueryTag.AssetGeofenceInteractions, id: assetId },
      // Clear all asset interactions summary queries
      { type: QueryTag.AssetInteractionsSummary },
      QueryTag.ConfigGroup,
      { type: QueryTag.Geofence, id: 'GEOFENCES' },
      { type: QueryTag.Metadata, id: 'VEHICLEGROUPS' },
      { type: QueryTag.AssetActivity, id: assetId },
      QueryTag.ScenariosStat,
      QueryTag.ScenariosTimeSeries,
      getHeatmapTag('GI', assetId),
      getHeatmapTag('VI', assetId),
    ],
    [assetId],
  );

  return (
    <>
      <Page
        header={
          <div className={styles.header}>
            <AssetIdentifier
              name={asset?.name}
              mismatch={asset?.stsNam === false}
              deviceType={asset?.hw ?? '0'}
              showIcon
            />
            <div className={styles.header__assetgroups}>{vehicleGroupChips}</div>
            <RefreshButton tagsToClear={tagsToClearOnRefresh} />
          </div>
        }
        nav={<NavTabs tabs={tabs} hasBorder />}
      >
        <Outlet />
      </Page>
      <ConnectivitySnackbar error={fetchError} message={t('FetchAssetError')} title={t('Error')} />
    </>
  );
}

export default SingleAsset;
